import { IcStatusLosange } from '@assets/icons/24x24';
import { createListCollection, Field as ChakraField, Fieldset, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { CockpitFormLayout } from '@components/cockpit/layouts/CockpitFormLayout';
import ModifiedTag from '@components/common/form/ModifiedField';
import { InputNumber } from '@components/ui/input/InputNumber';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { CustomSelect } from '@components/ui/select/Select';
import { Switch } from '@components/ui/switch';
import { getClearanceColor } from '@utils/data/notification.utils';
import { Field, useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { Organization } from '@/types/config/config.types';
import { DroneClearanceEnum } from '@/types/utm/drone.types';

export default function OrganizationSettingsForm() {
  const { initialValues, values, errors, touched, setFieldValue } = useFormikContext<Partial<Organization>>();

  const { formatMessage } = useIntl();
  return (
    <CockpitFormLayout title="components.organizations.settings.title" showSeparator={false}>
      <Fieldset.Root invalid={Object.keys(errors).some((key) => touched[key as keyof typeof touched])}>
        <VStack gap={5}>
          <VStack padding={4} gap={1.5} width="100%" border="2px solid" borderColor="neutral.850" alignItems="start">
            <HStack width="100%" alignItems="start">
              <Text whiteSpace="pre-line">
                <FormattedMessage id="components.organizations.settings.procedureAutoArchive" />
              </Text>
              <Switch
                size="sm"
                marginLeft="auto"
                checked={values.organizationParameter?.enableProcedureAutoArchive}
                onCheckedChange={() => {
                  setFieldValue(
                    'organizationParameter.enableProcedureAutoArchive',
                    !values.organizationParameter?.enableProcedureAutoArchive,
                  );
                }}
              />
            </HStack>
            {values.organizationParameter?.enableProcedureAutoArchive && (
              <>
                <Text color="neutral.300">
                  <FormattedMessage id="components.organizations.settings.expiredNotificationsTtl" />
                </Text>
                <Field
                  as={InputNumber}
                  name="organizationParameter.expiredNotificationsTtl"
                  id="expiredNotificationsTtl"
                  width="100px"
                  disable={!values.organizationParameter.enableProcedureAutoArchive}
                  min={0}
                  max={525960}
                  allowMouseWheel
                  onChange={(value?: number) => {
                    setFieldValue('organizationParameter.expiredNotificationsTtl', value);
                  }}
                  suffix="min"
                />
              </>
            )}
          </VStack>
          <ChakraField.Root variant="cockpit">
            <ChakraField.Label width="100%" height="20px">
              <FormattedMessage id="components.organizations.defaultClearance" />
              <RequiredIndicator />
              {initialValues.organizationParameter?.defaultClearance !==
                values.organizationParameter?.defaultClearance && <ModifiedTag />}
            </ChakraField.Label>
            <CustomSelect
              value={values.organizationParameter?.defaultClearance}
              collection={createListCollection({
                items: Object.values(DroneClearanceEnum).map((clearance) => ({
                  label: formatMessage({ id: `clearance.${clearance}` }),
                  value: clearance,
                })),
              })}
              formatLabel={(option) => (
                <HStack gap={1.5}>
                  <Icon as={IcStatusLosange} boxSize="24px" color={getClearanceColor(option.value)} />
                  <Text width="100%">{option.label}</Text>
                </HStack>
              )}
              onChange={(value) => setFieldValue('organizationParameter.defaultClearance', value)}
            />
          </ChakraField.Root>
        </VStack>
      </Fieldset.Root>
    </CockpitFormLayout>
  );
}
